import { useState } from 'react';
import { DollarSign } from 'lucide-react';
import { formatCurrencyOption } from '@/utils/formatters';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CurrencyInput } from '@/components/ui/currency-input';
import { useFxCurrencies } from '@/hooks/react-query/useFxRates';
import {
  ACCOUNTING_FRAMEWORK_LABELS,
  type AccountingFramework,
} from '@/types/fund';
import type { FundFormData } from './types';

interface FinancialInformationSectionProps {
  formData: FundFormData;
  savedLpAllocationMode: string;
  errors: Record<string, string>;
  onInputChange: (
    field: keyof FundFormData,
    value: string | number | null
  ) => void;
}

const DEFAULT_CURRENCY_OPTIONS = [
  { iso_code: 'EUR', name: 'Euro' },
  { iso_code: 'USD', name: 'US Dollar' },
  { iso_code: 'GBP', name: 'British Pound' },
  { iso_code: 'CHF', name: 'Swiss Franc' },
];

export function FinancialInformationSection({
  formData,
  savedLpAllocationMode,
  errors,
  onInputChange,
}: FinancialInformationSectionProps) {
  const { data: fxCurrencies = [] } = useFxCurrencies();
  const [isConfirmingProRata, setIsConfirmingProRata] = useState(false);

  const changeLpAllocationMode = (isDealByDeal: boolean) => {
    if (isDealByDeal) {
      onInputChange('lp_allocation_mode', 'DEAL_BY_DEAL');
      return;
    }
    if (savedLpAllocationMode === 'DEAL_BY_DEAL') {
      setIsConfirmingProRata(true);
      return;
    }
    onInputChange('lp_allocation_mode', 'PRO_RATA');
  };
  const currencyOptions =
    fxCurrencies.length > 0 ? fxCurrencies : DEFAULT_CURRENCY_OPTIONS;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-slate-700" />
          <CardTitle>Financial Information</CardTitle>
        </div>
        <CardDescription>
          Fund size, commitments, and currency settings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="base_currency">Base Currency *</Label>
            <Select
              value={formData.base_currency}
              onValueChange={(value) => onInputChange('base_currency', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencyOptions.map((currency) => (
                  <SelectItem key={currency.iso_code} value={currency.iso_code}>
                    {formatCurrencyOption(currency)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="accounting_framework">Accounting Framework</Label>
            <Select
              value={formData.accounting_framework}
              onValueChange={(value) =>
                onInputChange('accounting_framework', value)
              }
            >
              <SelectTrigger id="accounting_framework">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(
                  Object.keys(
                    ACCOUNTING_FRAMEWORK_LABELS
                  ) as AccountingFramework[]
                ).map((framework) => (
                  <SelectItem key={framework} value={framework}>
                    {ACCOUNTING_FRAMEWORK_LABELS[framework]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Decides how a closed deal&apos;s costs post: RJ and US GAAP
              capitalise them into the investment, IFRS expenses them. A pending
              deal&apos;s costs wait until it closes or breaks.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="lp_allocation_mode"
              checked={formData.lp_allocation_mode === 'DEAL_BY_DEAL'}
              onCheckedChange={(checked) =>
                changeLpAllocationMode(checked === true)
              }
            />
            <ConfirmationDialog
              open={isConfirmingProRata}
              onOpenChange={setIsConfirmingProRata}
              title="Turn off deal-by-deal allocation?"
              description="On save, custom LP splits on draft investments go back to pro-rata. Approved investments keep theirs."
              confirmLabel="Turn off"
              variant="destructive"
              onConfirm={() => {
                onInputChange('lp_allocation_mode', 'PRO_RATA');
                setIsConfirmingProRata(false);
              }}
            />
            <div>
              <Label htmlFor="lp_allocation_mode">
                Deal-by-deal allocation
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                LPs choose per investment how much of their commitment to use.
                Off: every investment is split pro-rata to commitment.
              </p>
            </div>
          </div>
          <div>
            <Label htmlFor="minimum_prepayment_pool">
              Minimum Prepayment Pool
            </Label>
            <CurrencyInput
              id="minimum_prepayment_pool"
              value={formData.minimum_prepayment_pool}
              onChange={(value) =>
                onInputChange('minimum_prepayment_pool', value)
              }
              currency={formData.base_currency}
              decimalPlaces={0}
              placeholder="100,000"
              className={errors.minimum_prepayment_pool ? 'border-red-500' : ''}
            />
            {errors.minimum_prepayment && (
              <p className="text-sm text-red-600 mt-1">
                {errors.minimum_prepayment_pool}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="target_size">Target Size</Label>
            <CurrencyInput
              id="target_size"
              value={formData.target_size}
              onChange={(value) => onInputChange('target_size', value)}
              currency={formData.base_currency}
              decimalPlaces={0}
              placeholder="200,000,000"
              className={errors.target_size ? 'border-red-500' : ''}
            />
            {errors.target_size && (
              <p className="text-sm text-red-600 mt-1">{errors.target_size}</p>
            )}
          </div>
          <div>
            <Label htmlFor="hard_cap">Hard Cap</Label>
            <CurrencyInput
              id="hard_cap"
              value={formData.hard_cap}
              onChange={(value) => onInputChange('hard_cap', value)}
              currency={formData.base_currency}
              decimalPlaces={0}
              placeholder="250,000,000"
              className={errors.hard_cap ? 'border-red-500' : ''}
            />
            {errors.hard_cap && (
              <p className="text-sm text-red-600 mt-1">{errors.hard_cap}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
